/**
 * MascotIntroSection
 *
 * A two-column section with a mascot illustration on the left and a title +
 * description on the right. The mascot sits on top of a solid color circle
 * with small decorative circles in the top-left corner.
 *
 * Used on consulting service pages as an intro section below the hero.
 *
 * Props:
 * - mascotSrc (string, required): URL of the mascot image.
 * - mascotAlt (string, required): Alt text for the mascot image.
 * - circleBg (string, optional): Tailwind background class for the circle behind
 *                                the mascot (e.g. "bg-orange", "bg-blue"). Defaults to "bg-orange".
 * - title (string | ReactNode, required): The heading text. An orange period is appended automatically.
 * - description (string | ReactNode, required): Paragraph content below the heading.
 */

const MascotIntroSection = ({
  mascotSrc,
  mascotAlt,
  circleBg = "bg-orange",
  title,
  description,
}) => {
  return (
    <section className="px-4 md:px-8 lg:px-16 xl:px-24 my-16 md:my-24 lg:my-32 xl:my-52">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center gap-12 md:gap-8 lg:gap-16">

        {/* Mascot column */}
        <div className="md:w-1/2 relative flex items-center justify-center py-8 md:py-12 lg:py-16">
          <img
            src="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/circles.svg"
            alt=""
            className="absolute -top-8 -left-4 md:-top-2 md:left-2 lg:left-4 lg:-top-1 w-20 md:w-28 lg:w-32"
          />
          <div className={`w-48 h-48 md:w-64 md:h-64 lg:w-80 lg:h-80 xl:w-96 xl:h-96 rounded-full ${circleBg} absolute`} />
          <img
            src={mascotSrc}
            alt={mascotAlt}
            className="relative z-10 w-48 md:w-64 lg:w-80 xl:w-96 object-contain"
            loading="lazy"
          />
        </div>

        {/* Text column */}
        <div className="md:w-1/2 text-center md:text-left">
          <img
            src="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/horizontal-line.svg"
            alt=""
            className="w-28 md:w-40 mx-auto md:mx-0 mb-4"
          />
          <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-blue-dark1 mb-6">
            {title}
          </h2>
          <p className="!text-sm md:!text-base xl:!text-xl leading-relaxed max-w-lg xl:max-w-2xl mx-auto md:mx-0">
            {description}
          </p>
        </div>
      </div>
    </section>
  );
};

export default MascotIntroSection;
